/**
 * Errores del API, compartidos por los módulos que hablan con el backend de
 * autenticación.
 *
 * POR QUÉ EL CÓDIGO DE ESTADO VIAJA EN EL ERROR. `/api/registro` distingue 400,
 * 401, 409 y 503, y cada uno pide una salida distinta en la pantalla: volver a
 * escribir el formulario, volver a entrar, avisar que la cuenta ya existe, o
 * decir que este servidor no tiene autenticación configurada. Si el cliente
 * aplanara todo a `Error`, la interfaz tendría que adivinar por el texto.
 *
 * EL MENSAJE DEL SERVIDOR NO SE SUSTITUYE. Los mensajes de `apps/api` ya están
 * redactados para que los lea una persona («esa matrícula ya está registrada
 * con otra cuenta…»). Reemplazarlos por un genérico del cliente sería perder
 * información que ya se pagó por tener.
 */

/** Respuesta del API con código de estado y su mensaje tal como llegó. */
export class ErrorApi extends Error {
  readonly estado: number

  constructor(estado: number, mensaje: string) {
    super(mensaje)
    this.name = 'ErrorApi'
    this.estado = estado
  }
}

/** El cuerpo no tiene la forma que el contrato declara. */
export class ErrorDeFormato extends Error {
  constructor(mensaje: string) {
    super(mensaje)
    this.name = 'ErrorDeFormato'
  }
}

export function esRegistro(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor)
}

/** Lee el cuerpo como JSON sin lanzar: un cuerpo ilegible es un dato más. */
export async function leerJson(respuesta: Response): Promise<unknown> {
  try {
    return await respuesta.json()
  } catch {
    return null
  }
}

/**
 * Extrae el campo `error` de una respuesta no exitosa. El API lo escribe así en
 * todas sus rutas; si faltara, devuelve `null` y quien llama pone el respaldo.
 */
export function mensajeDelCuerpo(cuerpo: unknown): string | null {
  if (!esRegistro(cuerpo)) return null
  const mensaje = cuerpo['error']
  return typeof mensaje === 'string' && mensaje.trim() !== '' ? mensaje : null
}

/** Traduce el fallo de `fetch` a algo que se pueda leer en pantalla. */
export function describirFalloDeRed(error: unknown): string {
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    return 'el servidor no respondió a tiempo'
  }
  if (error instanceof Error && error.message.trim() !== '') return error.message
  return 'error de red'
}

/** Texto para mostrar de cualquier error que llegue a la interfaz. */
export function textoDeError(error: unknown): string {
  if (error instanceof Error && error.message.trim() !== '') return error.message
  return 'Error inesperado en el cliente.'
}
